import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Group, LocationOn, RestartAlt, Schedule } from '@mui/icons-material';

import {
  schedulingService,
  ScheduleWindow,
  SchedulingConfig,
  SchedulingConfigCreate,
  SalesContact,
  WeeklySchedule,
} from '../../services/schedulingService';
import { projectService } from '../../services/projectService';

interface SchedulingConfigurationProps {
  onConfigurationChange?: () => void;
}

const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '17:00';
const CUSTOM_BOOKING_MESSAGE_MAX_LENGTH = 4096;

const WEEKDAYS = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const SCHEDULE_ROWS = [
  ...WEEKDAYS.map((day) => ({ key: String(day.value), label: day.label })),
  { key: 'holiday', label: 'Festivos Colombia' },
];

const normalizeTimeValue = (value?: string | null, fallback: string = DEFAULT_START_TIME) =>
  value ? value.slice(0, 5) : fallback;

const createScheduleWindow = (
  enabled: boolean = true,
  startTime: string = DEFAULT_START_TIME,
  endTime: string = DEFAULT_END_TIME
): ScheduleWindow => ({
  enabled,
  start_time: normalizeTimeValue(startTime, DEFAULT_START_TIME),
  end_time: normalizeTimeValue(endTime, DEFAULT_END_TIME),
});

const buildDefaultWeeklySchedule = (enabledDays: number[] = WEEKDAYS.map((day) => day.value)): WeeklySchedule => {
  const enabledSet = new Set(enabledDays);
  return WEEKDAYS.reduce<WeeklySchedule>((acc, day) => {
    acc[String(day.value)] = createScheduleWindow(enabledSet.has(day.value));
    return acc;
  }, {});
};

const buildDefaultHolidaySchedule = (enabled: boolean = true): ScheduleWindow => createScheduleWindow(enabled);

const getWindowKey = (window?: ScheduleWindow): string | null => {
  if (!window || !window.enabled || !window.start_time || !window.end_time) {
    return null;
  }
  return `${window.start_time}-${window.end_time}`;
};

const deriveAvailableDays = (weeklySchedule: WeeklySchedule): number[] =>
  WEEKDAYS.filter((day) => weeklySchedule[String(day.value)]?.enabled).map((day) => day.value);

const deriveLegacyTimes = (weeklySchedule: WeeklySchedule, holidaySchedule: ScheduleWindow) => {
  const openWeekdays = WEEKDAYS
    .map((day) => weeklySchedule[String(day.value)])
    .filter((window): window is ScheduleWindow => Boolean(window?.enabled && window.start_time && window.end_time));

  if (openWeekdays.length > 0) {
    const startTimes = openWeekdays.map((window) => window.start_time || DEFAULT_START_TIME).sort();
    const endTimes = openWeekdays.map((window) => window.end_time || DEFAULT_END_TIME).sort();
    return {
      start_time: startTimes[0] || DEFAULT_START_TIME,
      end_time: endTimes[endTimes.length - 1] || DEFAULT_END_TIME,
    };
  }

  if (holidaySchedule.enabled && holidaySchedule.start_time && holidaySchedule.end_time) {
    return {
      start_time: holidaySchedule.start_time,
      end_time: holidaySchedule.end_time,
    };
  }

  return {
    start_time: DEFAULT_START_TIME,
    end_time: DEFAULT_END_TIME,
  };
};

const applyScheduleToForm = (
  formData: SchedulingConfigCreate,
  weeklySchedule: WeeklySchedule,
  holidaySchedule: ScheduleWindow
): SchedulingConfigCreate => {
  const legacyTimes = deriveLegacyTimes(weeklySchedule, holidaySchedule);
  return {
    ...formData,
    available_days: deriveAvailableDays(weeklySchedule),
    start_time: legacyTimes.start_time,
    end_time: legacyTimes.end_time,
    weekly_schedule: weeklySchedule,
    holiday_schedule: holidaySchedule,
  };
};

const normalizeConfigSchedule = (config?: Partial<SchedulingConfigCreate> & Partial<SchedulingConfig>) => {
  const startTime = normalizeTimeValue(config?.start_time, DEFAULT_START_TIME);
  const endTime = normalizeTimeValue(config?.end_time, DEFAULT_END_TIME);
  const availableDays = config?.available_days && config.available_days.length > 0
    ? config.available_days
    : WEEKDAYS.map((day) => day.value);

  const baseWeeklySchedule = buildDefaultWeeklySchedule(availableDays);
  const weeklySchedule = { ...baseWeeklySchedule };

  if (config?.weekly_schedule) {
    Object.entries(config.weekly_schedule).forEach(([key, value]) => {
      weeklySchedule[key] = {
        enabled: value?.enabled ?? weeklySchedule[key]?.enabled ?? true,
        start_time: normalizeTimeValue(value?.start_time, weeklySchedule[key]?.start_time || startTime),
        end_time: normalizeTimeValue(value?.end_time, weeklySchedule[key]?.end_time || endTime),
      };
    });
  } else {
    WEEKDAYS.forEach((day) => {
      weeklySchedule[String(day.value)] = createScheduleWindow(
        availableDays.includes(day.value),
        startTime,
        endTime
      );
    });
  }

  const holidaySchedule = config?.holiday_schedule
    ? {
        enabled: config.holiday_schedule.enabled ?? true,
        start_time: normalizeTimeValue(config.holiday_schedule.start_time, startTime),
        end_time: normalizeTimeValue(config.holiday_schedule.end_time, endTime),
      }
    : buildDefaultHolidaySchedule(true);

  return {
    weeklySchedule,
    holidaySchedule,
  };
};

const formatWindowRange = (window?: ScheduleWindow) => {
  if (!window || !window.enabled || !window.start_time || !window.end_time) {
    return 'Cerrado';
  }
  return `${window.start_time} - ${window.end_time}`;
};

const buildScheduleSummary = (config: Partial<SchedulingConfig>) => {
  const { weeklySchedule, holidaySchedule } = normalizeConfigSchedule(config);
  const groups: Array<{ label: string; key: string; endDay: number }> = [];
  let currentStart: number | null = null;
  let currentEnd: number | null = null;
  let currentKey: string | null = null;

  WEEKDAYS.forEach((day) => {
    const key = getWindowKey(weeklySchedule[String(day.value)]);
    if (!key) {
      if (currentStart !== null && currentEnd !== null && currentKey) {
        const startLabel = WEEKDAYS[currentStart].label.toLowerCase();
        const endLabel = WEEKDAYS[currentEnd].label.toLowerCase();
        groups.push({
          label: currentStart === currentEnd ? startLabel : `${startLabel} a ${endLabel}`,
          key: currentKey,
          endDay: currentEnd,
        });
      }
      currentStart = null;
      currentEnd = null;
      currentKey = null;
      return;
    }

    if (currentKey === key && currentEnd !== null && day.value === currentEnd + 1) {
      currentEnd = day.value;
      return;
    }

    if (currentStart !== null && currentEnd !== null && currentKey) {
      const startLabel = WEEKDAYS[currentStart].label.toLowerCase();
      const endLabel = WEEKDAYS[currentEnd].label.toLowerCase();
      groups.push({
        label: currentStart === currentEnd ? startLabel : `${startLabel} a ${endLabel}`,
        key: currentKey,
        endDay: currentEnd,
      });
    }

    currentStart = day.value;
    currentEnd = day.value;
    currentKey = key;
  });

  if (currentStart !== null && currentEnd !== null && currentKey) {
    const startLabel = WEEKDAYS[currentStart].label.toLowerCase();
    const endLabel = WEEKDAYS[currentEnd].label.toLowerCase();
    groups.push({
      label: currentStart === currentEnd ? startLabel : `${startLabel} a ${endLabel}`,
      key: currentKey,
      endDay: currentEnd,
    });
  }

  const holidayKey = getWindowKey(holidaySchedule);
  if (holidayKey) {
    const sundayGroup = groups.find((group) => group.key === holidayKey && group.endDay === 6);
    if (sundayGroup) {
      sundayGroup.label = `${sundayGroup.label === 'domingo' ? 'domingos' : sundayGroup.label} y festivos`;
    } else {
      groups.push({ label: 'festivos', key: holidayKey, endDay: 7 });
    }
  }

  return groups.map((group) => `${group.label}: ${group.key}`).join(' / ');
};

const SchedulingConfiguration: React.FC<SchedulingConfigurationProps> = ({ onConfigurationChange }) => {
  const [configs, setConfigs] = useState<SchedulingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<{ project_code: string; project_name: string }[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SchedulingConfig | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [openDaysPreview, setOpenDaysPreview] = useState('');
  const [newOpenDate, setNewOpenDate] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SchedulingConfigCreate>(() => {
    const weeklySchedule = buildDefaultWeeklySchedule();
    const holidaySchedule = buildDefaultHolidaySchedule(true);
    return applyScheduleToForm(
      {
        project_code: '',
        project_name: '',
        showroom_location: '',
        showroom_address: '',
        booking_start_date: null,
        booking_end_date: null,
        open_booking_dates: [],
        custom_booking_message: null,
        available_days: WEEKDAYS.map((day) => day.value),
        start_time: DEFAULT_START_TIME,
        end_time: DEFAULT_END_TIME,
        weekly_schedule: weeklySchedule,
        holiday_schedule: holidaySchedule,
        slot_duration: 30,
        buffer_time: 0,
        slot_capacity: 1,
        max_daily_meetings: 8,
        advance_booking_days: 30,
        sales_team_contacts: [],
        special_instructions: '',
        is_active: true,
      },
      weeklySchedule,
      holidaySchedule
    );
  });
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<SalesContact | null>(null);
  const [contactForm, setContactForm] = useState<SalesContact>({
    name: '',
    phone: '',
    email: '',
    role: '',
    specialties: [],
  });

  useEffect(() => {
    loadConfigurations();
    loadProjects();
  }, []);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    const payload = {
      project_name: formData.project_name,
      booking_start_date: formData.booking_start_date,
      booking_end_date: formData.booking_end_date,
      open_booking_dates: formData.open_booking_dates || [],
      custom_booking_message: formData.custom_booking_message,
      available_days: formData.available_days,
      start_time: formData.start_time,
      end_time: formData.end_time,
      weekly_schedule: formData.weekly_schedule,
      holiday_schedule: formData.holiday_schedule,
    };

    let active = true;
    setPreviewLoading(formData.custom_booking_message === null || formData.custom_booking_message === undefined);
    setPreviewError(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await schedulingService.previewSchedulingCopy(payload);
        if (!active) {
          return;
        }
        setPreviewText(response.booking_intro_text ?? response.preview_text);
        setOpenDaysPreview(response.open_booking_text || '');
      } catch (err) {
        console.error('Error generating scheduling preview:', err);
        if (!active) {
          return;
        }
        setPreviewText('');
        setPreviewError('No fue posible generar la previsualización.');
      } finally {
        if (active) {
          setPreviewLoading(false);
        }
      }
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    dialogOpen,
    formData.project_name,
    formData.booking_start_date,
    formData.booking_end_date,
    JSON.stringify(formData.open_booking_dates),
    formData.custom_booking_message,
    JSON.stringify(formData.weekly_schedule),
    JSON.stringify(formData.holiday_schedule),
  ]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects(0, 1000);
      setProjectOptions(
        data.projects.map((project) => ({
          project_code: project.project_code,
          project_name: project.project_name,
        }))
      );
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await schedulingService.getSchedulingConfigs();
      setConfigs(data);
    } catch (err) {
      console.error('Error loading configurations:', err);
      setError('Error al cargar las configuraciones');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config?: SchedulingConfig) => {
    const normalized = normalizeConfigSchedule(config);

    if (config) {
      setEditingConfig(config);
      setFormData(
        applyScheduleToForm(
          {
            project_code: config.project_code,
            project_name: config.project_name,
            showroom_location: config.showroom_location,
            showroom_address: config.showroom_address || '',
            booking_start_date: config.booking_start_date ?? null,
            booking_end_date: config.booking_end_date ?? null,
            open_booking_dates: config.open_booking_dates || [],
            custom_booking_message: config.custom_booking_message ?? null,
            available_days: config.available_days,
            start_time: config.start_time,
            end_time: config.end_time,
            weekly_schedule: normalized.weeklySchedule,
            holiday_schedule: normalized.holidaySchedule,
            slot_duration: config.slot_duration,
            buffer_time: config.buffer_time,
            slot_capacity: config.slot_capacity ?? 1,
            max_daily_meetings: config.max_daily_meetings,
            advance_booking_days: config.advance_booking_days,
            sales_team_contacts: config.sales_team_contacts,
            special_instructions: config.special_instructions || '',
            is_active: config.is_active,
          },
          normalized.weeklySchedule,
          normalized.holidaySchedule
        )
      );
    } else {
      setEditingConfig(null);
      const weeklySchedule = buildDefaultWeeklySchedule();
      const holidaySchedule = buildDefaultHolidaySchedule(true);
      setFormData(
        applyScheduleToForm(
          {
            project_code: '',
            project_name: '',
            showroom_location: '',
            showroom_address: '',
            booking_start_date: null,
            booking_end_date: null,
        open_booking_dates: [],
            custom_booking_message: null,
            available_days: WEEKDAYS.map((day) => day.value),
            start_time: DEFAULT_START_TIME,
            end_time: DEFAULT_END_TIME,
            weekly_schedule: weeklySchedule,
            holiday_schedule: holidaySchedule,
            slot_duration: 30,
            buffer_time: 0,
            slot_capacity: 1,
            max_daily_meetings: 8,
            advance_booking_days: 30,
            sales_team_contacts: [],
            special_instructions: '',
            is_active: true,
          },
          weeklySchedule,
          holidaySchedule
        )
      );
    }

    setPreviewText('');
    setPreviewError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setPreviewText('');
    setPreviewError(null);
  };

  const updateScheduleWindow = (rowKey: string, updates: Partial<ScheduleWindow>) => {
    if (rowKey === 'holiday') {
      const nextHolidaySchedule = {
        ...(formData.holiday_schedule || buildDefaultHolidaySchedule(true)),
        ...updates,
      };
      const nextWeeklySchedule = { ...(formData.weekly_schedule || buildDefaultWeeklySchedule()) };
      setFormData(applyScheduleToForm(formData, nextWeeklySchedule, nextHolidaySchedule));
      return;
    }

    const nextWeeklySchedule = {
      ...(formData.weekly_schedule || buildDefaultWeeklySchedule()),
      [rowKey]: {
        ...(formData.weekly_schedule?.[rowKey] || createScheduleWindow(true)),
        ...updates,
      },
    };
    const nextHolidaySchedule = formData.holiday_schedule || buildDefaultHolidaySchedule(true);
    setFormData(applyScheduleToForm(formData, nextWeeklySchedule, nextHolidaySchedule));
  };

  const handleSaveConfiguration = async () => {
    try {
      if (editingConfig) {
        await schedulingService.updateSchedulingConfig(editingConfig.id, formData);
      } else {
        await schedulingService.createSchedulingConfig(formData);
      }

      await loadConfigurations();
      if (onConfigurationChange) {
        onConfigurationChange();
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving configuration:', err);
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((item: any) => `${(item.loc || []).join('.')}: ${item.msg}`).join('; ');
        setError(`Error de validación: ${messages}`);
      } else if (detail && typeof detail === 'object') {
        const baseMessage = detail.user_message || detail.message;
        const conflicts = Array.isArray(detail.conflicts) ? detail.conflicts : [];

        if (baseMessage && conflicts.length > 0) {
          const firstConflict = conflicts[0];
          const meetingDate = firstConflict?.meeting_date;
          const meetingId = firstConflict?.meeting_id;
          const conflictSuffix = meetingDate
            ? ` Primera cita en conflicto: #${meetingId ?? '-'} (${meetingDate}).`
            : '';
          setError(`${baseMessage}${conflictSuffix}`);
        } else if (baseMessage) {
          setError(baseMessage);
        } else {
          setError('Error al guardar la configuración');
        }
      } else if (typeof detail === 'string') {
        setError(`Error: ${detail}`);
      } else {
        setError('Error al guardar la configuración');
      }
    }
  };

  const handleDeleteConfiguration = async (configId: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta configuración?')) {
      try {
        await schedulingService.deleteSchedulingConfig(configId);
        await loadConfigurations();
        if (onConfigurationChange) {
          onConfigurationChange();
        }
      } catch (err) {
        console.error('Error deleting configuration:', err);
        setError('Error al eliminar la configuración');
      }
    }
  };

  const handleOpenContactDialog = (contact?: SalesContact) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({ ...contact });
    } else {
      setEditingContact(null);
      setContactForm({
        name: '',
        phone: '',
        email: '',
        role: '',
        specialties: [],
      });
    }
    setContactDialogOpen(true);
  };

  const handleSaveContact = () => {
    if (editingContact) {
      const updatedContacts = formData.sales_team_contacts.map((contact) =>
        contact === editingContact ? contactForm : contact
      );
      setFormData({ ...formData, sales_team_contacts: updatedContacts });
    } else {
      setFormData({
        ...formData,
        sales_team_contacts: [...formData.sales_team_contacts, contactForm],
      });
    }
    setContactDialogOpen(false);
  };

  const handleDeleteContact = (contactToDelete: SalesContact) => {
    const updatedContacts = formData.sales_team_contacts.filter((contact) => contact !== contactToDelete);
    setFormData({ ...formData, sales_team_contacts: updatedContacts });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Configuraciones de Citas</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Nueva Configuración
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {configs.map((config) => {
          const normalized = normalizeConfigSchedule(config);
          const openDays = deriveAvailableDays(normalized.weeklySchedule);
          return (
            <Grid item xs={12} md={6} lg={4} key={config.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="div">
                      {config.project_name}
                    </Typography>
                    <Chip
                      label={config.is_active ? 'Activo' : 'Inactivo'}
                      color={config.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Código: {config.project_code}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">{config.showroom_location}</Typography>
                  </Box>

                  <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2">{buildScheduleSummary(config)}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Group fontSize="small" color="action" />
                    <Typography variant="body2">
                      {config.sales_team_contacts.length} contacto(s) de ventas
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Días abiertos:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {openDays.map((day) => (
                        <Chip key={day} label={WEEKDAYS[day].label} size="small" variant="outlined" />
                      ))}
                      {normalized.holidaySchedule.enabled && (
                        <Chip label="Festivos" size="small" variant="outlined" color="secondary" />
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Duración: {config.slot_duration} min | Buffer: {config.buffer_time} min
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Capacidad/horario: {config.slot_capacity ?? 1} | Máx. diarias: {config.max_daily_meetings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inicio agenda: {config.booking_start_date || 'Desde hoy'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fin agenda: {config.booking_end_date || 'Sin fecha límite'}
                    {!!config.open_booking_dates?.length && ` · Sin hora fija: ${config.open_booking_dates.join(', ')}`}
                  </Typography>
                </CardContent>

                <CardActions>
                  <IconButton size="small" onClick={() => handleOpenDialog(config)} color="primary">
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteConfiguration(config.id)} color="error">
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={projectOptions}
                value={projectOptions.find((project) => project.project_code === formData.project_code) ?? null}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setFormData({
                      ...formData,
                      project_code: newValue.project_code,
                      project_name: newValue.project_name,
                    });
                  }
                }}
                getOptionLabel={(option) => `${option.project_name} (${option.project_code})`}
                isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
                disabled={Boolean(editingConfig)}
                renderInput={(params) => (
                  <TextField {...params} label="Proyecto" placeholder="Seleccionar proyecto" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ubicación del Showroom"
                value={formData.showroom_location}
                onChange={(event) => setFormData({ ...formData, showroom_location: event.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dirección del Showroom"
                value={formData.showroom_address}
                onChange={(event) => setFormData({ ...formData, showroom_address: event.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha inicio de agenda"
                value={formData.booking_start_date || ''}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    booking_start_date: event.target.value || null,
                  })
                }
                InputLabelProps={{ shrink: true }}
                helperText="Opcional. Si queda vacío, se usa desde hoy."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha fin de agenda"
                value={formData.booking_end_date || ''}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    booking_end_date: event.target.value || null,
                  })
                }
                inputProps={{ min: formData.booking_start_date || undefined }}
                InputLabelProps={{ shrink: true }}
                helperText="Opcional. Este es el último día disponible para citas presenciales."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Fechas de visita sin hora fija</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                En estas fechas se registra solo el día, sin límite de visitas, dentro del horario configurado. Las citas existentes conservan su hora.
              </Typography>
              <Box display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <TextField type="date" label="Añadir fecha sin hora fija" value={newOpenDate}
                  onChange={(event) => setNewOpenDate(event.target.value)} InputLabelProps={{ shrink: true }} />
                <Button disabled={!newOpenDate} onClick={() => {
                  setFormData({ ...formData, open_booking_dates: Array.from(new Set([...(formData.open_booking_dates || []), newOpenDate])).sort() });
                  setNewOpenDate('');
                }}>Añadir fecha</Button>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {(formData.open_booking_dates || []).map((day) => (
                  <Chip key={day} label={day} onDelete={() => setFormData({ ...formData, open_booking_dates: (formData.open_booking_dates || []).filter((value) => value !== day) })} />
                ))}
              </Box>
              {openDaysPreview && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>{openDaysPreview}</Typography>}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Horarios por día
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Grid container sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', fontWeight: 600 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Día
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      Abierto
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Hora inicio
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Hora fin
                    </Typography>
                  </Grid>
                </Grid>

                {SCHEDULE_ROWS.map((row, index) => {
                  const windowConfig = row.key === 'holiday'
                    ? (formData.holiday_schedule || buildDefaultHolidaySchedule(true))
                    : (formData.weekly_schedule?.[row.key] || createScheduleWindow(true));

                  return (
                    <Grid
                      container
                      key={row.key}
                      alignItems="center"
                      spacing={2}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderTop: index === 0 ? 'none' : '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">{row.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatWindowRange(windowConfig)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Switch
                          checked={windowConfig.enabled}
                          onChange={(event) => updateScheduleWindow(row.key, { enabled: event.target.checked })}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="time"
                          label="Inicio"
                          value={windowConfig.start_time || DEFAULT_START_TIME}
                          onChange={(event) =>
                            updateScheduleWindow(row.key, { start_time: event.target.value || DEFAULT_START_TIME })
                          }
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          disabled={!windowConfig.enabled}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="time"
                          label="Fin"
                          value={windowConfig.end_time || DEFAULT_END_TIME}
                          onChange={(event) =>
                            updateScheduleWindow(row.key, { end_time: event.target.value || DEFAULT_END_TIME })
                          }
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          disabled={!windowConfig.enabled}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                    <Typography variant="subtitle2">
                      Previsualización del mensaje para el chatbot
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RestartAlt />}
                      disabled={
                        formData.custom_booking_message === null
                        || formData.custom_booking_message === undefined
                      }
                      onClick={() => setFormData({ ...formData, custom_booking_message: null })}
                    >
                      Restaurar mensaje automático
                    </Button>
                  </Box>
                  {previewLoading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Generando mensaje...
                      </Typography>
                    </Box>
                  ) : previewError ? (
                    <Alert severity="warning">{previewError}</Alert>
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Mensaje de introducción para agendamiento presencial"
                      value={
                        formData.custom_booking_message !== null
                        && formData.custom_booking_message !== undefined
                          ? formData.custom_booking_message
                          : previewText
                      }
                      onChange={(event) =>
                        setFormData({ ...formData, custom_booking_message: event.target.value })
                      }
                      inputProps={{ maxLength: CUSTOM_BOOKING_MESSAGE_MAX_LENGTH }}
                      helperText={
                        formData.custom_booking_message !== null
                        && formData.custom_booking_message !== undefined
                          ? `Mensaje personalizado · ${formData.custom_booking_message.length}/${CUSTOM_BOOKING_MESSAGE_MAX_LENGTH} caracteres`
                          : 'Mensaje automático generado desde la configuración del proyecto'
                      }
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Duración de Cita (min)"
                value={formData.slot_duration}
                onChange={(event) => setFormData({ ...formData, slot_duration: parseInt(event.target.value, 10) || 30 })}
                inputProps={{ min: 15, max: 240 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Tiempo Buffer (min)"
                value={formData.buffer_time}
                onChange={(event) => setFormData({ ...formData, buffer_time: parseInt(event.target.value, 10) || 0 })}
                inputProps={{ min: 0, max: 60 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Capacidad por Horario"
                value={formData.slot_capacity ?? 1}
                onChange={(event) =>
                  setFormData({ ...formData, slot_capacity: parseInt(event.target.value, 10) || 1 })
                }
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Máx. Citas Diarias"
                value={formData.max_daily_meetings}
                onChange={(event) =>
                  setFormData({ ...formData, max_daily_meetings: parseInt(event.target.value, 10) || 1 })
                }
                inputProps={{ min: 1, max: 2000 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2">Equipo de Ventas</Typography>
                <Button size="small" startIcon={<Add />} onClick={() => handleOpenContactDialog()}>
                  Agregar Contacto
                </Button>
              </Box>

              {formData.sales_team_contacts.map((contact, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {contact.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {contact.phone} | {contact.email} | {contact.role}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenContactDialog(contact)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteContact(contact)} color="error">
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Instrucciones Especiales"
                value={formData.special_instructions}
                onChange={(event) => setFormData({ ...formData, special_instructions: event.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch
                  checked={formData.is_active}
                  onChange={(event) => setFormData({ ...formData, is_active: event.target.checked })}
                />
                <Typography variant="body2">Configuración Activa</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveConfiguration} variant="contained">
            {editingConfig ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? 'Editar Contacto' : 'Nuevo Contacto'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={contactForm.name}
                onChange={(event) => setContactForm({ ...contactForm, name: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={contactForm.phone}
                onChange={(event) => setContactForm({ ...contactForm, phone: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={contactForm.email}
                onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cargo/Rol"
                value={contactForm.role}
                onChange={(event) => setContactForm({ ...contactForm, role: event.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveContact} variant="contained">
            {editingContact ? 'Actualizar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchedulingConfiguration;
